import type { components } from "@/core/api/schema";
import { addDays, formatClock } from "@/core/format/format";
import { dayPauseClosesAt, zonedMoment } from "@/core/format/zoned-time";

/** One of the brand's own meal slots, with its cutoff (Brand → Meal slots). */
export type MealSlot = components["schemas"]["MealSlot"];
/** A slot as plans, menus and meals name it. */
export type MealSlotRef = components["schemas"]["MealSlotRef"];

/** Slots in the brand's order of a day. */
export const inSlotOrder = <T extends { position: number }>(slots: readonly T[]) =>
  [...slots].sort((a, b) => a.position - b.position);

/** "Lunch, Dinner": the slots in the brand's order. */
export const formatSlots = (slots: readonly MealSlotRef[]) =>
  inSlotOrder(slots)
    .map((s) => s.name)
    .join(", ");

/** "10:30 am the same day" / "9:00 pm the day before". */
export const describeCutoff = (slot: Pick<MealSlot, "cutoffDaysBefore" | "cutoffTime">) =>
  `${formatClock(slot.cutoffTime)} ${slot.cutoffDaysBefore === 1 ? "the day before" : "the same day"}`;

/**
 * When a meal on `date` in this slot closes (its dish chosen, or the meal paused): the slot's cutoff on that day
 * or the day before, in the brand's zone (the API's rule, DECISIONS.md §6).
 */
export const slotClosesAt = (
  date: string,
  slot: Pick<MealSlot, "cutoffDaysBefore" | "cutoffTime">,
  timeZone: string,
): Date => zonedMoment(addDays(date, -slot.cutoffDaysBefore), slot.cutoffTime, timeZone);

/**
 * When a day stops being changeable as a whole (holidays, whole-day pauses): the day-pause cutoff the evening
 * before, or earlier if one of the brand's turned-on slots closes first (the API's rule).
 */
export function dayLocksAt(
  date: string,
  dayPauseCutoffTime: string,
  slots: readonly MealSlot[],
  timeZone: string,
): Date {
  const times = slots.filter((s) => s.active).map((s) => slotClosesAt(date, s, timeZone).getTime());
  return new Date(Math.min(dayPauseClosesAt(date, dayPauseCutoffTime, timeZone).getTime(), ...times));
}
